import { DOMAIN_STATUS } from '../settings/domains/constants';
import {
    getMarketingSendersRawList,
    normalizeMarketingSendersResponse,
} from './marketingSenderOptions';

export function hostFromEmail(email) {
    if (typeof email !== 'string') {
        return null;
    }
    const s = email.trim().toLowerCase();
    const i = s.lastIndexOf('@');
    return i > 0 && i < s.length - 1 ? s.slice(i + 1) : null;
}

export function getVerifiedHostsFromDomains(domains) {
    const list = Array.isArray(domains) ? domains : [];
    return list
        .filter((d) => d?.status === DOMAIN_STATUS.RUNNING && d?.domain)
        .map((d) => String(d.domain).trim().toLowerCase());
}

export function isSenderDomainVerified(sender, verifiedHosts) {
    const host = hostFromEmail(sender.senderEmail);
    return host != null && verifiedHosts.includes(host);
}

/**
 * Keeps only senders whose email domain matches a verified (RUNNING) provider domain.
 * Same rule as marketing settings senders table (`domainVerified`).
 */
export function filterMarketingSendersByVerifiedDomain(rawSenders, domains) {
    const verifiedHosts = getVerifiedHostsFromDomains(domains);
    const list = Array.isArray(rawSenders) ? rawSenders : [];
    return list.filter((s) => isSenderDomainVerified(s, verifiedHosts));
}

function getFulfilledArray(result) {
    if (result?.status !== 'fulfilled' || result.value === undefined || result.value === null) {
        return [];
    }
    if (Array.isArray(result.value)) {
        return result.value;
    }
    return [];
}

/**
 * Parses `Promise.allSettled([listSenders(), listDomains()])` results into normalized inputs.
 * Shared by marketing settings senders and campaign sender Select.
 *
 * @param {PromiseSettledResult<unknown>} sendersResult
 * @param {PromiseSettledResult<unknown>} domainsResult
 * @returns {{ rawSenders: object[], domains: unknown[] }}
 */
export function getMarketingSendersAndDomainsFromSettled(sendersResult, domainsResult) {
    const sendersData = sendersResult?.status === 'fulfilled' ? sendersResult.value : null;
    const rawSenders = getMarketingSendersRawList(sendersData);
    const domains = getFulfilledArray(domainsResult);
    return { rawSenders, domains };
}

/**
 * All sender rows with `domainVerified` (settings senders table).
 *
 * @param {object[]} rawSenders
 * @param {unknown[]} domains
 * @returns {object[]}
 */
export function enrichSendersWithDomainVerification(rawSenders, domains) {
    const verifiedHosts = getVerifiedHostsFromDomains(domains);
    const list = Array.isArray(rawSenders) ? rawSenders : [];
    return list.map((sender) => ({
        ...sender,
        domainVerified: isSenderDomainVerified(sender, verifiedHosts),
    }));
}

/**
 * Campaign form Select options: only senders on a verified domain.
 *
 * @param {PromiseSettledResult<unknown>} sendersResult
 * @param {PromiseSettledResult<unknown>} domainsResult
 */
export function buildVerifiedMarketingSenderSelectOptions(sendersResult, domainsResult) {
    const { rawSenders, domains } = getMarketingSendersAndDomainsFromSettled(
        sendersResult,
        domainsResult,
    );
    const filtered = filterMarketingSendersByVerifiedDomain(rawSenders, domains);
    return normalizeMarketingSendersResponse(filtered);
}
